import React, { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';
import 'flatpickr/dist/flatpickr.min.css';
import { Icon } from '@iconify/react';
import { AppFilterField } from './AppFilterBar';

const toYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseYmd = (value) => {
  const raw = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const date = new Date(`${raw}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

function useFlatpickrSingle({ value, onChange, minDate, maxDate, disabled }) {
  const inputRef = useRef(null);
  const pickerRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!inputRef.current) return undefined;

    pickerRef.current = flatpickr(inputRef.current, {
      mode: 'single',
      locale: French,
      dateFormat: 'd/m/Y',
      allowInput: true,
      disableMobile: true,
      defaultDate: parseYmd(value) ?? undefined,
      onChange: (selectedDates) => {
        onChangeRef.current?.(selectedDates[0] ? toYmd(selectedDates[0]) : '');
      },
    });

    return () => {
      pickerRef.current?.destroy();
      pickerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    const next = parseYmd(value);
    const current = picker.selectedDates[0] ? toYmd(picker.selectedDates[0]) : '';
    const wanted = next ? toYmd(next) : '';
    if (current === wanted) return;
    if (!next) {
      picker.clear(false);
      return;
    }
    picker.setDate(next, false);
  }, [value]);

  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    picker.set('minDate', parseYmd(minDate) ?? null);
    picker.set('maxDate', parseYmd(maxDate) ?? null);
  }, [minDate, maxDate]);

  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    if (disabled) picker._input?.setAttribute('disabled', 'disabled');
    else picker._input?.removeAttribute('disabled');
  }, [disabled]);

  return inputRef;
}

/**
 * Deux dates indépendantes :
 * - début : facile à changer (1 clic)
 * - fin : optionnelle, vide = pas de borne
 */
export default function AppFilterDateRange({
  id = 'filter-date-range',
  label = 'Période',
  dateDebut = '',
  dateFin = '',
  onChange,
  col = 'col-12 col-md-5 col-lg-4',
  disabled = false,
  className = '',
}) {
  const debutRef = useFlatpickrSingle({
    value: dateDebut,
    disabled,
    maxDate: dateFin || undefined,
    onChange: (nextDebut) => {
      onChange?.({
        dateDebut: nextDebut,
        dateFin: dateFin && nextDebut && dateFin < nextDebut ? '' : dateFin,
      });
    },
  });

  const finRef = useFlatpickrSingle({
    value: dateFin,
    disabled,
    minDate: dateDebut || undefined,
    onChange: (nextFin) => {
      onChange?.({ dateDebut, dateFin: nextFin });
    },
  });

  return (
    <AppFilterField col={col} label={label} htmlFor={`${id}-debut`} className={className}>
      <div className="app-filter-date-range d-flex align-items-center gap-1">
        <input
          ref={debutRef}
          id={`${id}-debut`}
          type="text"
          className="form-control form-control-sm"
          placeholder="À partir du"
          disabled={disabled}
          autoComplete="off"
          aria-label="Date de début"
        />
        <span className="app-filter-date-range__sep text-secondary-light flex-shrink-0">→</span>
        <div className="app-filter-date-range__end position-relative flex-grow-1">
          <input
            ref={finRef}
            id={`${id}-fin`}
            type="text"
            className="form-control form-control-sm"
            placeholder="Jusqu'au (optionnel)"
            disabled={disabled}
            autoComplete="off"
            aria-label="Date de fin"
          />
          {dateFin ? (
            <button
              type="button"
              className="btn btn-link btn-sm app-filter-date-range__clear p-0"
              title="Effacer la date de fin"
              aria-label="Effacer la date de fin"
              disabled={disabled}
              onClick={() => onChange?.({ dateDebut, dateFin: '' })}
            >
              <Icon icon="mdi:close" width={14} height={14} />
            </button>
          ) : null}
        </div>
      </div>
    </AppFilterField>
  );
}

export { toYmd as formatFilterDateYmd, parseYmd as parseFilterDateYmd };
